import PageHeader from "@/components/common/PageHeader";
import { monitoringService } from "@/services/monitoring.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, Mail, Pencil, Plus, Save, Trash2, X } from "lucide-react";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";

type CommunicationTemplate = {
  id: string;
  name: string;
  channel: "email" | "in_app" | "sms";
  subject?: string | null;
  body: string;
  variables: string[];
  category: string;
  isActive: boolean;
  updatedAt: string;
};

const emptyForm = {
  name: "",
  channel: "email" as CommunicationTemplate["channel"],
  subject: "",
  body: "",
  category: "support",
  isActive: true,
};

function variablesIn(...values: string[]) {
  const found = new Set<string>();
  values.forEach((value) => {
    for (const match of value.matchAll(/\{\{(\w+)\}\}/g)) found.add(match[1]);
  });
  return [...found];
}

export default function CommunicationTemplatesPage() {
  const queryClient = useQueryClient();
  const [channel, setChannel] = useState("all");
  const [category, setCategory] = useState("all");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [preview, setPreview] = useState<{
    subject?: string | null;
    body: string;
  } | null>(null);

  const templates = useQuery({
    queryKey: ["communication-templates", channel, category],
    queryFn: () =>
      monitoringService.getTemplates({
        channel: channel === "all" ? undefined : channel,
        category: category === "all" ? undefined : category,
        limit: 100,
      }),
  });
  const variables = useMemo(
    () => variablesIn(form.subject, form.body),
    [form.subject, form.body],
  );
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["communication-templates"] });
  const reset = () => {
    setEditingId(null);
    setForm(emptyForm);
    setPreview(null);
  };

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        ...form,
        subject:
          form.channel === "email" ? form.subject || undefined : undefined,
        variables,
      };
      return editingId
        ? monitoringService.updateTemplate(editingId, payload)
        : monitoringService.createTemplate(payload);
    },
    onSuccess: () => {
      void refresh();
      reset();
      toast.success(editingId ? "Template updated" : "Template created");
    },
    onError: () => toast.error("Template could not be saved"),
  });
  const remove = useMutation({
    mutationFn: monitoringService.deleteTemplate,
    onSuccess: () => {
      void refresh();
      toast.success("Template deleted");
    },
    onError: () => toast.error("Template could not be deleted"),
  });
  const previewMutation = useMutation({
    mutationFn: () =>
      monitoringService.previewTemplate(form.body, form.subject, variables),
    onSuccess: setPreview,
    onError: () => toast.error("Preview failed"),
  });

  const beginEdit = (template: CommunicationTemplate) => {
    setEditingId(template.id);
    setForm({
      name: template.name,
      channel: template.channel,
      subject: template.subject ?? "",
      body: template.body,
      category: template.category,
      isActive: template.isActive,
    });
    setPreview(null);
  };

  const rows = (templates.data?.data ?? []) as CommunicationTemplate[];
  return (
    <div className="space-y-6">
      <PageHeader
        title="Communications"
        subtitle="Govern reusable email, in-app and SMS templates with variable-aware previews."
      />

      <div className="grid gap-6 xl:grid-cols-[420px_1fr]">
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-bold text-slate-900">
              {editingId ? (
                <Pencil className="h-4 w-4" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              {editingId ? "Edit template" : "New template"}
            </h2>
            {editingId && (
              <button
                onClick={reset}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-50"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="mt-4 space-y-3">
            <Field
              label="Name"
              value={form.name}
              onChange={(value) => setForm({ ...form, name: value })}
            />
            <div className="grid grid-cols-2 gap-3">
              <Select
                label="Channel"
                value={form.channel}
                values={["email", "in_app", "sms"]}
                onChange={(value) =>
                  setForm({
                    ...form,
                    channel: value as CommunicationTemplate["channel"],
                  })
                }
              />
              <Select
                label="Category"
                value={form.category}
                values={[
                  "billing",
                  "feature",
                  "maintenance",
                  "marketing",
                  "support",
                ]}
                onChange={(value) => setForm({ ...form, category: value })}
              />
            </div>
            {form.channel === "email" && (
              <Field
                label="Subject"
                value={form.subject}
                onChange={(value) => setForm({ ...form, subject: value })}
              />
            )}
            <label className="block">
              <span className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
                Body
              </span>
              <textarea
                value={form.body}
                onChange={(event) =>
                  setForm({ ...form, body: event.target.value })
                }
                rows={8}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder="Hello {{firstName}}, ..."
              />
            </label>
            <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-500">
              Variables:{" "}
              {variables.length
                ? variables.map((item) => `{{${item}}}`).join(", ")
                : "none detected"}
            </div>
            {editingId && (
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(event) =>
                    setForm({ ...form, isActive: event.target.checked })
                  }
                />{" "}
                Active
              </label>
            )}
            <div className="flex gap-2">
              <button
                disabled={!form.name || !form.body || save.isPending}
                onClick={() => save.mutate()}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-slate-900 py-2.5 text-xs font-bold text-white disabled:opacity-50"
              >
                <Save className="h-4 w-4" />{" "}
                {editingId ? "Save version" : "Create template"}
              </button>
              <button
                disabled={!form.body || previewMutation.isPending}
                onClick={() => previewMutation.mutate()}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 text-xs font-bold text-slate-700 disabled:opacity-50"
              >
                <Eye className="h-4 w-4" /> Preview
              </button>
            </div>
            {preview && (
              <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-4">
                <p className="text-[10px] font-bold uppercase text-blue-500">
                  Rendered preview
                </p>
                {preview.subject && (
                  <p className="mt-2 font-bold text-slate-900">
                    {preview.subject}
                  </p>
                )}
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">
                  {preview.body}
                </p>
              </div>
            )}
          </div>
        </section>

        <section className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex gap-2">
              <Select
                label="Channel"
                value={channel}
                values={["all", "email", "in_app", "sms"]}
                onChange={setChannel}
                compact
              />
              <Select
                label="Category"
                value={category}
                values={[
                  "all",
                  "billing",
                  "feature",
                  "maintenance",
                  "marketing",
                  "support",
                ]}
                onChange={setCategory}
                compact
              />
            </div>
            <p className="text-xs text-slate-500">
              {templates.data?.total ?? 0} templates
            </p>
          </div>
          {templates.isLoading ? (
            <TemplatePanel>Loading communication templates…</TemplatePanel>
          ) : rows.length ? (
            rows.map((template) => (
              <TemplatePanel key={template.id}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Mail className="h-4 w-4 text-blue-600" />
                      <p className="font-bold text-slate-900">
                        {template.name}
                      </p>
                      <Badge value={template.channel} />
                      <Badge value={template.category} />
                      <Badge
                        value={template.isActive ? "active" : "disabled"}
                      />
                    </div>
                    {template.subject && (
                      <p className="mt-2 text-sm font-medium text-slate-700">
                        {template.subject}
                      </p>
                    )}
                    <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-sm text-slate-500">
                      {template.body}
                    </p>
                    <p className="mt-2 text-xs text-slate-400">
                      Variables: {template.variables?.join(", ") || "none"} ·
                      Updated {new Date(template.updatedAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <button
                      onClick={() => beginEdit(template)}
                      className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
                      aria-label={`Edit ${template.name}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() =>
                        window.confirm(`Delete ${template.name}?`) &&
                        remove.mutate(template.id)
                      }
                      className="rounded-lg p-2 text-red-500 hover:bg-red-50"
                      aria-label={`Delete ${template.name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </TemplatePanel>
            ))
          ) : (
            <TemplatePanel>No templates match these filters.</TemplatePanel>
          )}
        </section>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
        {label}
      </span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
      />
    </label>
  );
}

function Select({
  label,
  value,
  values,
  onChange,
  compact = false,
}: {
  label: string;
  value: string;
  values: string[];
  onChange: (value: string) => void;
  compact?: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
        {label}
      </span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={`h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm ${compact ? "min-w-32" : "w-full"}`}
      >
        {values.map((item) => (
          <option key={item} value={item}>
            {item.replace("_", " ")}
          </option>
        ))}
      </select>
    </label>
  );
}

function TemplatePanel({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      {children}
    </div>
  );
}

function Badge({ value }: { value: string }) {
  return (
    <span
      className={`rounded-full px-2 py-1 text-[9px] font-bold uppercase ${value === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}
    >
      {value}
    </span>
  );
}
